import React, { useRef, useEffect, useState } from 'react'
import { localizeLink } from '../../utils'
import {
  poundsToKilograms,
  stonesAndPoundsToKilograms,
  feetAndInchesToCentimeters,
  kcalToKJ,
} from '../../metricsConverter'
import { trackStartRegistration } from '../../analytics'
import useLocale from 'js/hooks/useLocale'

type OdometerWrapperProps = {
  recommendedCalories: number
  preferKcal: boolean
}
const OdometerWrapper = ({
  recommendedCalories,
  preferKcal,
}: OdometerWrapperProps) => {
  const Odometer = require('odometer')
  const odometerElementRef = useRef<HTMLInputElement>(null!)
  const odometerRef = useRef<{ update: (number) => void }>(null!)

  useEffect(() => {
    odometerRef.current = new Odometer({
      el: odometerElementRef.current,
      value: 1000,
      format: ',ddd',
    }) as { update: () => void }
  }, [Odometer])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      odometerRef.current.update(
        preferKcal ? recommendedCalories : kcalToKJ(recommendedCalories),
      )
    }, 50)
    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [recommendedCalories, preferKcal])

  return (
    <div className="c-cal-intake__result-number" ref={odometerElementRef} />
  )
}

const palExamples = [
  'E.g. no physical activity at work or leisure.',
  'E.g. little physical activity at work or leisure.',
  'E.g. moderate physical activity at work or ~30 min bi-daily exercising',
  'E.g. semi-heavy physical activity at work or ~30 min daily exercising.',
  'E.g. heavy physical activity at work or 60+ min daily exercising.',
]

const CalorieIntakeCalculator = () => {
  const metricWeightRef = useRef<HTMLInputElement>(null)
  const metricHeightRef = useRef<HTMLInputElement>(null)
  const imperialWeightRef = useRef<HTMLInputElement>(null)
  const imperialFeetRef = useRef<HTMLSelectElement>(null)
  const imperialInchesRef = useRef<HTMLSelectElement>(null)
  const britishStonesRef = useRef<HTMLSelectElement>(null)
  const britishPoundsRef = useRef<HTMLSelectElement>(null)
  const ageRef = useRef<HTMLInputElement>(null)
  const ctaRef = useRef<HTMLInputElement>(null)
  const [system, setSystem] = useState<'metric' | 'imperial' | 'british'>(
    'metric',
  )
  const [valid, setValid] = useState(false)
  const [recommendedCalories, setRecommendedCalories] = useState<
    number | false
  >() // This also toggles the result view)
  const [age, setAge] = useState('')
  const [goal, setGoal] = useState('maintain') // Lose = -500, gain = +50)
  const [gender, setGender] = useState('female') // ±201 for f, ±213 for )
  const [_bmr, setBmr] = useState<number | null>(null)
  const [pal, setPal] = useState(1.375) // Physical activity level, 1.2–1.)
  const [palExample, setPalExample] = useState(palExamples[2])
  const [bmi, setBmi] = useState<number | null>(null) // Between 18.5–30 == ±201/213, otherwise disclaime)
  const [preferKcal, setPreferKcal] = useState(true)
  const [metricWeight, setMetricWeight] = useState('')
  const [metricHeight, setMetricHeight] = useState('')
  const [imperialWeight, setImperialWeight] = useState('')
  const [imperialFeet, setImperialFeet] = useState('5')
  const [imperialInches, setImperialInches] = useState('0')
  const [britishStones, setBritishStones] = useState('10')
  const [britishPounds, setBritishPounds] = useState('0')
  const { language } = useLocale()

  useEffect(() => {
    const pageLang = language
    let locale = navigator.languages as Array<string> | string

    // If the localizationHelper returns english (i.e. not sv, nl, fr or any other country that uses the metric system)
    if (pageLang === 'en' && locale) {
      if (locale[0]) {
        locale = locale[0].toLowerCase()
      }

      if (locale === 'en-us') {
        setSystem('imperial')
        // UK uses stone and pounds (not only pounds as the US)
      } else if (locale === 'en-gb') {
        setSystem('british')
      }
    }
  }, [language])

  const toggleSubmitButton = () => {
    let valid = true

    if (ageRef.current?.value === '') {
      valid = false
    }

    if (metricWeightRef.current) {
      if (metricWeightRef.current.value === '') {
        valid = false
      }
    }

    if (imperialWeightRef.current) {
      if (imperialWeightRef.current.value === '') {
        valid = false
      }
    }

    if (metricHeightRef.current) {
      if (metricHeightRef.current.value === '') {
        valid = false
      }
    }

    setValid(valid)
  }

  const setUnits = (event) => {
    event.preventDefault()
    const system = event.currentTarget.getAttribute('data-value')
    setSystem(system)
    toggleSubmitButton()
  }
  const goBack = (event) => {
    event.preventDefault()

    setRecommendedCalories(false)
  }
  const controlInputs = (event) => {
    event.persist()

    switch (event.target) {
      case metricWeightRef.current:
        setMetricWeight(event.target.value)
        break
      case metricHeightRef.current:
        setMetricHeight(event.target.value)
        break
      case imperialWeightRef.current:
        setImperialWeight(event.target.value)
        break
      case imperialFeetRef.current:
        setImperialFeet(event.target.value)
        break
      case imperialInchesRef.current:
        setImperialInches(event.target.value)
        break
      case britishStonesRef.current:
        setBritishStones(event.target.value)
        break
      case britishPoundsRef.current:
        setBritishPounds(event.target.value)
        break
      case ageRef.current:
        setAge(event.target.value)
        break
    }
  }

  const calculate = () => {
    let bmi: number = 0
    let bmr: number = 0

    // Clean input
    const parse = (v) => parseFloat(v.replace(/,/, /./))

    if (system === 'metric') {
      bmi =
        (parse(metricWeight) / (parse(metricHeight) * parse(metricHeight))) *
        10_000
      bmr =
        10 * parse(metricWeight) +
        6.25 * parse(metricHeight) -
        5 * Number(age) +
        (gender === 'female' ? -161 : 5)
    } else if (system === 'imperial') {
      const feetAndInchesInCm = feetAndInchesToCentimeters(
        parse(imperialFeet),
        parse(imperialInches),
      )
      bmi =
        (poundsToKilograms(parse(imperialWeight)) /
          (feetAndInchesInCm * feetAndInchesInCm)) *
        10_000

      bmr =
        10 * poundsToKilograms(parse(imperialWeight)) +
        6.25 * feetAndInchesInCm -
        5 * Number(age) +
        (gender === 'female' ? -161 : 5)
    } else if (system === 'british') {
      const stoneAndPoundsInKilos = stonesAndPoundsToKilograms(
        parse(britishStones),
        parse(britishPounds),
      )

      bmi =
        (stoneAndPoundsInKilos / (parse(metricHeight) * parse(metricHeight))) *
        10_000

      bmr =
        10 * stoneAndPoundsInKilos +
        6.25 * parse(metricHeight) -
        5 * Number(age) +
        (gender === 'female' ? -161 : 5)
    }

    let recommendedCalories = bmr * pal

    if (goal === 'lose') recommendedCalories -= 500
    if (goal === 'gain') recommendedCalories += 500

    bmi = Math.round(bmi * 10) / 10

    setBmi(bmi)
    setBmr(bmr)
    setRecommendedCalories(recommendedCalories)

    window.setTimeout(() => {
      if (ctaRef.current) {
        ctaRef.current.classList.add('c-cal-intake__cta--appear')
      }
    }, 3000)
  }
  const submit = (event) => {
    event.preventDefault()
    calculate()
  }

  const calculateConfidenceRange = () => {
    let range

    if (gender === 'female') {
      range = '201'
    } else {
      range = '213'
    }

    if (preferKcal) {
      range += ' kcal'
    } else {
      range = kcalToKJ(parseInt(range, 10)) + ' kJ'
    }

    return range
  }

  const selectGender = (event) => {
    setGender(event.target.value)
  }

  const selectGoal = (event) => {
    setGoal(event.target.value)
  }

  const switchEnergySystem = () => {
    setPreferKcal(!preferKcal)
  }

  const updatePAL = (event) => {
    let palExample = palExamples[0]
    const value = parseFloat(event.target.value)

    if (value > 1.2) palExample = palExamples[0]
    if (value > 1.25) palExample = palExamples[1]
    if (value > 1.35) palExample = palExamples[2]
    if (value > 1.48) palExample = palExamples[3]
    if (value > 1.62) palExample = palExamples[4]

    setPal(event.target.value)
    setPalExample(palExample)
  }

  return (
    <div className="c-calculator  c-cal-intake">
      <h2 className="c-calculator__heading">Calorie Intake Calculator</h2>
      {!recommendedCalories && (
        <div className="c-calculator__inner">
          <form action="/" onSubmit={submit}>
            <div className="c-calculator__switcher">
              <button
                type="button"
                className={
                  system === 'metric'
                    ? 'c-calculator__switcher-button c-calculator__switcher-button--active'
                    : 'c-calculator__switcher-button'
                }
                onClick={setUnits}
                data-value="metric"
              >
                KG/CM
              </button>
              <button
                type="button"
                className={
                  system === 'imperial'
                    ? 'c-calculator__switcher-button c-calculator__switcher-button--active'
                    : 'c-calculator__switcher-button'
                }
                onClick={setUnits}
                data-value="imperial"
              >
                LBS/FT
              </button>
              <button
                type="button"
                className={
                  system === 'british'
                    ? 'c-calculator__switcher-button c-calculator__switcher-button--active'
                    : 'c-calculator__switcher-button'
                }
                onClick={setUnits}
                data-value="british"
              >
                ST/CM
              </button>
            </div>
            <div className="c-cal-intake__input-wrapper">
              {system === 'metric' && (
                <div className="c-cal-intake__input" data-name="weight">
                  <label htmlFor="weight">Your weight (kg)</label>
                  <input
                    id="weight"
                    onKeyUp={toggleSubmitButton}
                    onChange={controlInputs}
                    value={metricWeight}
                    type="number"
                    step="0.1"
                    ref={metricWeightRef}
                  />
                </div>
              )}
              {system === 'metric' && (
                <div className="c-cal-intake__input" data-name="height">
                  <label htmlFor="height">Your height (cm)</label>
                  <input
                    id="height"
                    onKeyUp={toggleSubmitButton}
                    onChange={controlInputs}
                    value={metricHeight}
                    type="number"
                    ref={metricHeightRef}
                  />
                </div>
              )}
              {system === 'imperial' && (
                <div className="c-cal-intake__input" data-name="weight">
                  <label htmlFor="weight">Your weight (lbs)</label>
                  <input
                    id="weight"
                    onKeyUp={toggleSubmitButton}
                    onChange={controlInputs}
                    value={imperialWeight}
                    type="number"
                    ref={imperialWeightRef}
                  />
                </div>
              )}
              {system === 'imperial' && (
                <div data-name="height" className="c-cal-intake__input-split">
                  <div className="c-cal-intake__input">
                    <span className="c-cal-intake__fake-label">
                      Your height
                    </span>
                    <label className="sr-only" htmlFor="feet">
                      Feet
                    </label>
                    <select
                      id="feet"
                      onChange={controlInputs}
                      value={imperialFeet}
                      ref={imperialFeetRef}
                    >
                      <option value="4">4&prime;</option>
                      <option value="5">5&prime;</option>
                      <option value="6">6&prime;</option>
                    </select>
                  </div>
                  <div className="c-cal-intake__input">
                    <label className="sr-only" htmlFor="inches">
                      Inches
                    </label>
                    <select
                      onChange={controlInputs}
                      value={imperialInches}
                      id="inches"
                      ref={imperialInchesRef}
                    >
                      <option value="0">0&Prime;</option>
                      <option value="1">1&Prime;</option>
                      <option value="2">2&Prime;</option>
                      <option value="3">3&Prime;</option>
                      <option value="4">4&Prime;</option>
                      <option value="5">5&Prime;</option>
                      <option value="6">6&Prime;</option>
                      <option value="7">7&Prime;</option>
                      <option value="8">8&Prime;</option>
                      <option value="9">9&Prime;</option>
                      <option value="10">10&Prime;</option>
                      <option value="11">11&Prime;</option>
                    </select>
                  </div>
                </div>
              )}
              {system === 'british' && (
                <div data-name="weight" className="c-cal-intake__input-split">
                  <div className="c-cal-intake__input">
                    <span className="c-cal-intake__fake-label">
                      Your weight
                    </span>
                    <label className="sr-only" htmlFor="stones">
                      Stones
                    </label>
                    <select
                      onChange={controlInputs}
                      value={britishStones}
                      id="stones"
                      ref={britishStonesRef}
                    >
                      <option value="7">7 st</option>
                      <option value="8">8 st</option>
                      <option value="9">9 st</option>
                      <option value="10">10 st</option>
                      <option value="11">11 st</option>
                      <option value="12">12 st</option>
                      <option value="13">13 st</option>
                      <option value="14">14 st</option>
                      <option value="15">15 st</option>
                      <option value="16">16 st</option>
                      <option value="17">17 st</option>
                      <option value="18">18 st</option>
                      <option value="19">19 st</option>
                      <option value="20">20 st</option>
                      <option value="21">21 st</option>
                      <option value="22">22 st</option>
                      <option value="23">23 st</option>
                      <option value="24">24 st</option>
                      <option value="25">25 st</option>
                      <option value="26">26 st</option>
                      <option value="27">27 st</option>
                      <option value="28">28 st</option>
                      <option value="29">29 st</option>
                      <option value="30">30 st</option>
                      <option value="31">31 st</option>
                      <option value="32">32 st</option>
                      <option value="33">33 st</option>
                      <option value="34">34 st</option>
                      <option value="35">35 st</option>
                      <option value="36">36 st</option>
                      <option value="37">37 st</option>
                      <option value="38">38 st</option>
                    </select>
                  </div>
                  <div className="c-cal-intake__input">
                    <label className="sr-only" htmlFor="pounds">
                      Pounds
                    </label>
                    <select
                      onChange={controlInputs}
                      value={britishPounds}
                      id="pounds"
                      ref={britishPoundsRef}
                    >
                      <option value="0">0 lb</option>
                      <option value="1">1 lb</option>
                      <option value="2">2 lb</option>
                      <option value="3">3 lb</option>
                      <option value="4">4 lb</option>
                      <option value="5">5 lb</option>
                      <option value="6">6 lb</option>
                      <option value="7">7 lb</option>
                      <option value="8">8 lb</option>
                      <option value="9">9 lb</option>
                      <option value="10">10 lb</option>
                      <option value="11">11 lb</option>
                    </select>
                  </div>
                </div>
              )}
              {system === 'british' && (
                <div className="c-cal-intake__input" data-name="height">
                  <label htmlFor="weight">Your height (cm)</label>
                  <input
                    id="weight"
                    onKeyUp={toggleSubmitButton}
                    onChange={controlInputs}
                    value={metricHeight}
                    type="number"
                    ref={metricHeightRef}
                  />
                </div>
              )}
              <div className="c-cal-intake__input" data-name="age">
                <label htmlFor="weight">Age</label>
                <input
                  id="age"
                  onKeyUp={toggleSubmitButton}
                  onChange={controlInputs}
                  value={age}
                  type="number"
                  ref={ageRef}
                />
              </div>
              <fieldset className="c-cal-intake__radios" data-name="gender">
                <legend className="c-cal-intake__legend">Gender</legend>
                <div className="c-radio  c-cal-intake__radios-radio">
                  <input
                    className="c-radio__input"
                    type="radio"
                    name="gender"
                    checked={gender === 'female'}
                    onChange={selectGender}
                    value="female"
                    id="female"
                  />
                  <label className="c-radio__label" htmlFor="female">
                    <span>F</span>
                  </label>
                </div>
                <div className="c-radio  c-cal-intake__radios-radio">
                  <input
                    className="c-radio__input"
                    type="radio"
                    checked={gender === 'male'}
                    name="gender"
                    onChange={selectGender}
                    value="male"
                    id="male"
                  />
                  <label className="c-radio__label" htmlFor="male">
                    <span>M</span>
                  </label>
                </div>
              </fieldset>
              <fieldset className="c-cal-intake__radios" data-name="goal">
                <legend className="c-cal-intake__legend">
                  I want to <span className="c-cal-intake__text-gap">…</span>{' '}
                  weight
                </legend>
                <div className="c-radio  c-cal-intake__radios-radio">
                  <input
                    className="c-radio__input"
                    type="radio"
                    name="goal"
                    checked={goal === 'lose'}
                    onChange={selectGoal}
                    value="lose"
                    id="lose"
                  />
                  <label className="c-radio__label" htmlFor="lose">
                    <span>Lose</span>
                  </label>
                </div>
                <div className="c-radio  c-cal-intake__radios-radio">
                  <input
                    className="c-radio__input"
                    type="radio"
                    name="goal"
                    checked={goal === 'maintain'}
                    onChange={selectGoal}
                    value="maintain"
                    id="maintain"
                  />
                  <label className="c-radio__label" htmlFor="maintain">
                    <span>Maintain</span>
                  </label>
                </div>
                <div className="c-radio  c-cal-intake__radios-radio">
                  <input
                    className="c-radio__input"
                    type="radio"
                    name="goal"
                    checked={goal === 'gain'}
                    onChange={selectGoal}
                    value="gain"
                    id="gain"
                  />
                  <label className="c-radio__label" htmlFor="gain">
                    <span>Gain</span>
                  </label>
                </div>
              </fieldset>
              <div
                className="c-cal-intake__input c-cal-intake__input--range"
                data-name="pal"
              >
                <label htmlFor="pal">Activity level</label>
                <div className="c-cal-intake__range-wrapper">
                  <span className="c-cal-intake__range-legend">Low</span>
                  <div className="c-cal-intake__range-input">
                    <input
                      type="range"
                      id="pal"
                      min="1.2"
                      max="1.7"
                      step="0.01"
                      value={pal}
                      onChange={updatePAL}
                    />
                  </div>
                  <span className="c-cal-intake__range-legend">High</span>
                </div>
              </div>
            </div>
            <p className="c-cal-intake__range-example">{palExample}</p>
            <button
              className="c-button c-button--primary  c-calculator__submit"
              type="submit"
              disabled={valid ? undefined : true}
            >
              <span>Calculate</span>
            </button>
          </form>
        </div>
      )}
      {recommendedCalories && (
        <div className="c-calculator__inner">
          <div className="c-cal-intake__result">
            <button className="c-calculator__back" onClick={goBack}>
              <span className="sr-only">Close result</span>
            </button>
            <h3 className="c-cal-intake__result-heading">
              Recommended daily {preferKcal ? 'calories' : ' kilojoules'}
            </h3>
            <OdometerWrapper
              preferKcal={preferKcal}
              recommendedCalories={recommendedCalories}
            />
            <button
              className="c-cal-intake__energy-switcher"
              onClick={switchEnergySystem}
            >
              <span>Switch to {preferKcal ? 'kJ' : 'kcal'}</span>
            </button>
            <div className="c-cal-intake__cta" ref={ctaRef}>
              <p>Track your calorie intake with our app:</p>
              <a
                className="c-button c-button--primary"
                href={localizeLink('/signup/', language)}
                onClick={() => {
                  trackStartRegistration()
                }}
              >
                <span>Sign up for Lifesum</span>
              </a>
            </div>
            <small className="c-cal-intake__fine-print">
              {bmi && (bmi < 18.5 || bmi > 30) && (
                <span>
                  Your BMI ({bmi}) isn’t within the same range as the
                  participants in the study to develop the equation behind this
                  approximation. Our recommendation may thusly be inaccurate for
                  you.
                </span>
              )}
              {bmi && bmi >= 18.5 && bmi <= 30 && (
                <span>
                  This is an approximation and may be wrong by ±
                  {calculateConfidenceRange()} depending on your muscle mass,
                  fat mass and metabolism.
                </span>
              )}
            </small>
          </div>
        </div>
      )}
    </div>
  )
}

export default CalorieIntakeCalculator
